/**
 * Metric column definitions for the creative performance grid + table, and the ABSOLUTE tone model
 * for the metric chips.
 *
 * Tone is a property of the creative alone — it is derived from that row's own numbers against fixed
 * thresholds, never from the other rows on screen. A basis relative to the visible set makes the
 * colour a statement about scroll position rather than about the creative (a 3.56× ROAS renders red
 * once enough stronger rows load above it), and it repaints already-read cards every time a page
 * loads. `toneFor` therefore takes a ROW and no result set, so a relative basis cannot come back by
 * accident.
 *
 * Only two metrics carry a verdict, because only two have a defensible absolute standard:
 *
 *   - **ROAS** — a unitless ratio with a real zero point (1.00× = you got your money back) and a real
 *     target (break-even = 1 / contribution margin). One scale is fair across brands and currencies.
 *   - **Cost per purchase** — money, so it has no scale of its own (€40 is excellent for a sofa and
 *     ruinous for a lip balm). Its only absolute basis is the value of a purchase, and
 *     `purchase value ÷ cost per purchase` IS the ROAS — so it inherits that row's ROAS verdict and
 *     the two chips can never contradict each other.
 *
 * Everything else is neutral: spend, purchase value and impressions are volume rather than
 * efficiency (there is no spend that is "good"), and cost per result has no comparable threshold
 * because the underlying result differs per objective.
 */
import {
    CPA_OWN_PAST_SEVERE_MULTIPLE,
    CREATIVE_METRIC_CATALOG,
    CREATIVE_METRIC_RAMP_DIRECTION,
    SIGNIFICANCE_MIN_GAP,
    VERDICT_PURCHASES_FLOOR,
    bandJudgesOutcome,
    viewThroughShare,
    ANNOTATION_STATUS_LABEL,
    type IAnnotationStatus,
    type ICreativeMetricEntry,
    type ICreativeMetricKey,
} from 'shared'

import {
    costPerResultTypeLabel,
    resultTypeLabel,
} from '@/features/agents/agent-panel/agent-panel-employees/channel-insights-result-type-label'
import {
    capContributions,
    costPerResultCardParts,
    costPerResultContributions,
    formatContribution,
    type IContributionOverflow,
} from '@/features/creative-performance/creative-performance-contributions'

import { formatSpend } from '@/features/creative-intelligence/utils/creative-intelligence-format'
import type { ICreativePerformanceRow } from '@/api/creative-intelligence/get-creative-performance-query'
import {
    DASH,
    formatMetricValue,
    type IMetricValueOverrides,
    type IMetricValueRow,
} from '@/features/creative-performance/creative-metric-format'
import { relativeMetricTone } from '@/features/creative-performance/creative-performance-relative-tone'
import type { GroupAggregateRow } from '@/features/creative-performance/creative-performance-view-helpers'
import type { IMetricToneResult } from '@/features/creative-performance/creative-performance-tone'
import type {
    ICreativePerformanceFieldId,
    ICreativePerformanceSortRule,
} from '@/features/creative-performance/creative-performance-analysis-state'

/**
 * Every metric a card can carry — DERIVED from the shared catalog, never restated here.
 *
 * A hand-written union was the second list this surface maintained, and it is why the card offered
 * seven metrics while the row carried thirty-two and the table rendered twenty-nine of them: nothing
 * failed when the lists diverged, the metric was simply unreachable. Widening the catalog now widens
 * this, and every exhaustive `switch` over it breaks at compile time — which is the desired failure.
 */
export type IGridMetricKey = ICreativeMetricKey

/**
 * A catalog entry whose `key` is narrowed to the metric union.
 *
 * The catalog's own interface types `key` as `string` so an entry can be written without repeating
 * the union; the const assertion on the array is what recovers the literals. This alias carries that
 * narrowing to consumers, so a picker can pass `entry.key` straight to a toggle handler instead of
 * casting a string back into the union it came from.
 */
export type ICardMetricEntry = ICreativeMetricEntry & { key: IGridMetricKey }

/**
 * The ROAS bands. Each boundary is the break-even point of a round contribution margin
 * (`break-even = 1 / margin`, the same rule the media-buying gates enforce), which keeps every edge
 * explainable in one sentence rather than being a percentile of some benchmark table.
 */
/** Below this the creative returned less revenue than it cost — unprofitable at any margin. */
export const ROAS_LOSING = 1
/** Break-even at a 50% contribution margin; the floor most performance marketers treat as a minimum. */
export const ROAS_BREAK_EVEN = 2
/** Break-even at a ~33% margin, and the bottom of the commonly cited 2.5–4× healthy band. */
export const ROAS_HEALTHY = 3

/**
 * Purchases required before a ratio earns a verdict at all. A 3.5× off one sale is noise, and a green
 * chip invites acting on it. Mirrors `SEGMENT_MIN_CONVERSIONS` in the API's `media-buying-gates.ts`
 * — deliberately far below that file's `DATA_SUFFICIENCY_MIN_CONVERSIONS` (50), which is the bar for
 * moving budget, not for tinting a chip.
 *
 * RE-EXPORTED, not restated: the detail panel's band model gates its own purchase-derived verdicts on
 * the same floor, and two constants for one rule in two packages is exactly the drift the shared band
 * table exists to prevent. The value lives in `shared`.
 */
export const MIN_PURCHASES_FOR_TONE = VERDICT_PURCHASES_FLOOR

const neutral = (reason: string): IMetricToneResult => ({ tone: 'neutral', reason })

/**
 * The absolute verdict for one creative's return, and the reason to show for it. The confidence floor
 * is applied first: a ratio nobody should act on gets no colour, whatever it says.
 */
export function roasBand(
    roas: number | null,
    conversions: number,
    /**
     * Whether the return was computed from a PURCHASE. The bands below are calibrated on purchase
     * economics — `ROAS_BREAK_EVEN` is break-even at a 50% contribution margin on a sale — and there
     * is no published anchor for "return on spend from trials". Applying them anyway would put a
     * green chip on a number nobody calibrated. `undefined` means the caller does not know the
     * outcome and reads as do-not-withhold, which is today's behaviour.
     */
    isPurchaseOutcome?: boolean
): IMetricToneResult {
    if (roas === null) return neutral('No conversions in this window — no return to judge.')
    if (!bandJudgesOutcome('roas', isPurchaseOutcome)) {
        return neutral('No calibrated band for this account\'s outcome — the return is shown without a verdict.')
    }
    if (!(conversions >= MIN_PURCHASES_FOR_TONE)) {
        return neutral(
            `Only ${conversions} ${conversions === 1 ? 'conversion' : 'conversions'} — too few to judge a return (needs ${MIN_PURCHASES_FOR_TONE}).`
        )
    }
    if (roas < ROAS_LOSING) {
        return { tone: 'bad', reason: `Below ${ROAS_LOSING.toFixed(2)}x — returned less than it cost.` }
    }
    if (roas < ROAS_BREAK_EVEN) {
        return {
            tone: 'warn',
            reason: `Below ${ROAS_BREAK_EVEN.toFixed(2)}x — above spend, but under break-even on a 50% contribution margin.`,
        }
    }
    if (roas < ROAS_HEALTHY) {
        return neutral(
            `${ROAS_BREAK_EVEN.toFixed(2)}–${ROAS_HEALTHY.toFixed(2)}x — around the e-commerce median. Profitable on a healthy margin, thin on a tight one.`
        )
    }
    return { tone: 'good', reason: `${ROAS_HEALTHY.toFixed(2)}x or better — clears break-even on a ~33% margin.` }
}

export interface IGridMetricDef {
    key: IGridMetricKey
    label: string
    /**
     * Display string for the cell. Reads the metric projection only — a grouped header's aggregate
     * row (a `GroupAggregateRow`) passes through the same formatter as a full creative row.
     */
    format: (row: GroupAggregateRow, currency: string) => string
    /** This metric's absolute verdict for one row. Volume metrics always return neutral. */
    tone: (row: GroupAggregateRow) => IMetricToneResult
    /**
     * The per-contributor lines to show when this metric's row disagrees with itself, or nothing for
     * a metric that is always one number. Declared on the definition so the surfaces stay a loop over
     * `GRID_METRICS` rather than each carrying a branch on one metric's key.
     */
    parts?: (row: GroupAggregateRow, currency: string) => ICardMetricParts | undefined
}

/** The stacked form of a disagreeing metric: what to show, and what it left out. */
export interface ICardMetricParts {
    parts: { key: string; label: string; value: string }[]
    overflow?: IContributionOverflow
}

/**
 * The two values that are not a function of their unit alone, and are therefore named rather than
 * folded into a formatter.
 *
 * A blended cost-per-result across differing objectives is not a small number or a missing one — it
 * is incommensurable, so it says so. An em dash there would read as "not measured", which is a
 * different and wrong claim.
 */
/** A finite number from a wire value, or `null` — the shape every derivation divides with. */
const num = (value: unknown): number | null => {
    if (value === null || value === undefined) return null
    const n = Number(value)
    return Number.isFinite(n) ? n : null
}

/**
 * A quotient, or `null` wherever it cannot be one.
 *
 * A zero, absent or non-finite denominator yields `null`, never `0` and never `Infinity`: a rate with
 * no denominator is unmeasured, and rendering it as zero asserts a measured failure the data does not
 * support.
 */
function quotient(numerator: unknown, denominator: unknown): number | null {
    const a = num(numerator)
    const b = num(denominator)
    if (a === null || b === null || b === 0) return null
    return a / b
}

/**
 * The account's own outcome count, whatever it converts on.
 *
 * `terminalConversions` first, because a lead-gen or trials advertiser has no purchases by
 * definition and dividing by them would blank the metric for a third of the base. The purchase
 * column is the fallback for a stale cached row written before the terminal event was resolved.
 */
const outcomeCount = (row: IMetricValueRow) => num(row.terminalConversions) ?? num(row.purchaseConversions)

/**
 * A materially adverse move against the row's own past, on the house significance gap.
 *
 * The same threshold the media-buying gates use to refuse to call a winner: a move too small to be a
 * winner is too small to be fatigue.
 */
const MATERIAL_MOVE = SIGNIFICANCE_MIN_GAP

/**
 * The fatigue reading for one row, or `null` when it has no comparable past.
 *
 * Both legs must fire. Falling click-through alone is a creative that was always weak; rising cost
 * alone is an auction that got dearer. Together, against the SAME creative's preceding window, they
 * are the pattern the doctrine calls fatigue — and even then the flag says "look", not "kill",
 * because the full call needs audience data a single row cannot carry.
 */
function fatigueReading(row: IMetricValueRow): string | null {
    const ctrChange = num(row.outboundCtrChange)
    const costRatio = num(row.cpaVsOwnPast) ?? (num(row.cpaChange) === null ? null : 1 + (num(row.cpaChange) as number))
    if (ctrChange === null || costRatio === null) return null
    const clicksFalling = ctrChange <= -MATERIAL_MOVE
    const costRising = costRatio >= 1 + MATERIAL_MOVE
    if (clicksFalling && costRising) {
        return costRatio >= CPA_OWN_PAST_SEVERE_MULTIPLE ? 'Fatigued' : 'Tiring'
    }
    return 'Holding'
}

/**
 * A creative's status, derived from its own numbers rather than from someone having tagged it.
 *
 * `annotationStatus` already carries this vocabulary, but it is HAND-SET: on a fleet where almost
 * nobody labels, the column is empty for almost every creative, which is worse than absent — a
 * reader learns to ignore it. This fills it from measurement.
 *
 * Precedence is human-first, and that is not a courtesy. A person who opened the creative knows
 * things the rule cannot see — a brand-safety problem, a seasonal push, a client instruction — so a
 * hand-set label always wins, including when it contradicts the numbers.
 *
 * WITHHELD (null), never guessed, when the brand set no target: `cpaVsTarget` is the whole basis of
 * the verdict, and a target nobody agreed to would make every creative look on- or off-plan.
 *
 * Below the confidence floor it reads TESTING rather than a verdict. A cost-per figure computed from
 * two conversions is noise, and dressing noise as "Loser" is how a working creative gets killed.
 */
function derivedStatusReading(row: IMetricValueRow): string | null {
    // The no-target withhold comes FIRST. Checking the hand-set label before it would make this
    // column a verbatim copy of the "Status" column on any brand with no target — two columns with
    // identical values, one of which claims in its tooltip to be derived from the numbers.
    const vsTarget = num(row.cpaVsTarget)
    if (vsTarget === null) return null

    const hand = row.annotationStatus
    if (typeof hand === 'string' && hand.length > 0) return ANNOTATION_STATUS_LABEL[hand as IAnnotationStatus] ?? hand
    // `null` outcomes means the account measures none, which is BELOW the floor, not above it.
    if ((outcomeCount(row) ?? 0) < VERDICT_PURCHASES_FLOOR) return ANNOTATION_STATUS_LABEL.TESTING

    const spendMove = num(row.spendChange)
    if (vsTarget <= 1 - SIGNIFICANCE_MIN_GAP) {
        // Beating the target AND taking materially more budget than last window is the one case
        // "scaling" names — it is a statement about the trajectory, not just the level.
        return spendMove !== null && spendMove >= MATERIAL_MOVE
            ? ANNOTATION_STATUS_LABEL.SCALING
            : ANNOTATION_STATUS_LABEL.WINNER
    }
    if (vsTarget >= 1 + SIGNIFICANCE_MIN_GAP) return ANNOTATION_STATUS_LABEL.LOSER
    // Inside the band either way: the gap is not big enough to call, which is its own answer.
    return ANNOTATION_STATUS_LABEL.TESTING
}

/**
 * The metrics computed here rather than carried on the wire.
 *
 * Every operand is already on the row, so sending the quotient too would widen every response for no
 * new information. Each is `null` on a zero or absent denominator — see `quotient`.
 */
const VALUE_OVERRIDES: IMetricValueOverrides = {
    costPerOutboundClick: (row) => {
        const perClick = quotient(row.spendMicros, row.outboundClicks)
        return perClick === null ? null : Math.round(perClick)
    },
    costPerLandingPageView: (row) => {
        const perView = quotient(row.spendMicros, row.landingPageViews)
        return perView === null ? null : Math.round(perView)
    },
    clickToPurchase: (row) => quotient(outcomeCount(row), row.outboundClicks),
    cartToPurchase: (row) => quotient(outcomeCount(row), row.addsToCart),
    hookToClick: (row) => quotient(row.outboundClicks, row.threeSecondPlays),
    fatigueFlag: (row) => fatigueReading(row),
    derivedStatus: (row) => derivedStatusReading(row),
    // NOT `quotient(...)`: a quotient cannot tell an unmeasured split from a measured zero, and both
    // arrive here as 0. The shared formula returns `null` for the first — which the unit formatter
    // renders as an em dash — and 0 for the second, which renders as `0%`. That difference is the
    // entire metric: `0%` asserts every conversion was clicked, and asserting it about a row nobody
    // measured is the failure this exists to prevent.
    viewThroughShare: (row) =>
        viewThroughShare(
            row.viewThroughConversions as number | undefined,
            row.results as number | undefined,
            Boolean(row.splitMeasured)
        ),
}

/**
 * The values that are not a function of their unit alone.
 *
 * A creative spanning differing objectives has no ONE cost per result, so the single-value form is
 * the joined contribution list rather than a placeholder standing in for all of them. The overflow
 * marker is NOT appended here: a caller that truncates renders it beside the text
 * (`contributionMarker`), where clipping cannot eat it.
 */
const FORMAT_OVERRIDES: Readonly<Record<string, (row: IMetricValueRow, currency: string) => string>> = {
    costPerResult: (row, currency) => {
        const source = row as unknown as GroupAggregateRow
        const contributions = costPerResultContributions(source)
        if (contributions.length > 1) {
            return capContributions(contributions, (part) => part.label)
                .shown.map((part) => `${part.label} ${formatContribution(part, currency)}`)
                .join(', ')
        }
        return row.costPerResultMicros === null || row.costPerResultMicros === undefined
            ? DASH
            : formatSpend(Number(row.costPerResultMicros), currency)
    },
}

/** Per-metric multi-part rendering, for the surfaces that can lay contributions out on their own lines. */
const PARTS_BUILDERS: Readonly<
    Record<string, (row: GroupAggregateRow, currency: string) => ICardMetricParts | undefined>
> = {
    costPerResult: (row, currency) => costPerResultCardParts(row, currency),
}

/**
 * The verdict for one catalog entry on one row.
 *
 * Three paths, and the order matters. The return's own band is bespoke because its gating is
 * bespoke — the conversion floor, the outcome check and the lowered-floor disclosure are specific to
 * it and are already tested. A cost-per-outcome INHERITS that verdict rather than computing one, so
 * the two chips can never contradict each other. Everything else defers to the relative-tone module,
 * which is the one place that decides whether a column has a basis at all; when it declines, the
 * catalog's own note explains the absence rather than leaving an uncoloured chip unexplained.
 */
function toneForEntry(entry: ICreativeMetricEntry, row: GroupAggregateRow): IMetricToneResult {
    // Account-relative gradient owns colour for these keys — discrete ROAS/doctrine bands removed.
    if (entry.key in CREATIVE_METRIC_RAMP_DIRECTION) {
        return neutral("Coloured against your account's 90-day average when anchors are available.")
    }
    if (entry.verdict.kind === 'none') {
        return (
            relativeMetricTone(entry.key, row as unknown as ICreativePerformanceRow) ??
            neutral(entry.verdict.note)
        )
    }
    return (
        relativeMetricTone(entry.key, row as unknown as ICreativePerformanceRow) ??
        neutral('No basis to judge this creative on — the figure is shown without a verdict.')
    )
}

/**
 * Every card metric, projected from the shared catalog.
 *
 * A projection, not a list: the catalog is the only place a metric is declared, and this file
 * supplies the two things the catalog deliberately does not carry — how a unit renders, and how a
 * verdict is reached. That is what makes "a metric's verdict survives the switch from card to row"
 * true by construction, since the table projects the same entries through the same two functions.
 */
export const GRID_METRICS: IGridMetricDef[] = CREATIVE_METRIC_CATALOG.map((entry) => ({
    key: entry.key,
    label: entry.label,
    format: (row, currency) =>
        formatMetricValue(entry, row as IMetricValueRow, currency, FORMAT_OVERRIDES, VALUE_OVERRIDES),
    ...(PARTS_BUILDERS[entry.key] ? { parts: PARTS_BUILDERS[entry.key] } : {}),
    tone: (row) => toneForEntry(entry, row),
}))

export const GRID_METRIC_BY_KEY = Object.fromEntries(GRID_METRICS.map((m) => [m.key, m])) as Record<
    IGridMetricKey,
    IGridMetricDef
>

/**
 * The catalog ENTRY behind a card metric — its group, formula, unit and requirements.
 *
 * The picker needs the declaration, not the projection: it groups by funnel stage, shows the formula
 * on hover, and hides a metric the account cannot compute. Re-exported through this module so no
 * component reaches past the surface's own metric layer into `shared` for the same fact.
 */
export const CREATIVE_METRIC_BY_GRID_KEY: Readonly<Record<IGridMetricKey, ICardMetricEntry>> = Object.fromEntries(
    CREATIVE_METRIC_CATALOG.map((entry) => [entry.key, entry])
) as Record<IGridMetricKey, ICardMetricEntry>

/**
 * The metric columns shown by default, matching the mockup's initial chip selection.
 *
 * Read BEFORE any row set has loaded, so it cannot depend on what the account measures. It stays the
 * purchase-shaped list because that is right for 68.6% of accounts and because a default that
 * changed under the reader once data arrived would be worse than one that is occasionally wrong.
 * `defaultMetricKeysFor` refines it once the outcome model is known.
 */
export const DEFAULT_METRIC_KEYS: IGridMetricKey[] = ['spend', 'roas', 'purchaseValue', 'impressions']

/**
 * The terminal event's name in each of the places an outcome metric needs it.
 *
 * A purchase advertiser keeps the literal words buyers use — `Purchases`, `Cost / purchase`,
 * `Purchase value`, `AOV`, `ROAS`. Generalising those would be a regression for the 68.6% of the base
 * that sells, so the branch is deliberate rather than an omission.
 *
 * Lives HERE rather than in the detail panel because the grid, the table and the panel must name the
 * same event the same way — two label paths for one fact is exactly the drift a shared builder
 * prevents. The panel imports it (child → parent); nothing imports across sibling feature folders.
 */
export function outcomeMetricLabels(model?: { isPurchase: boolean; terminalActionType: string | null } | null) {
    if (!model || model.isPurchase || !model.terminalActionType) {
        return { count: 'Purchases', cost: 'Cost / purchase', value: 'Purchase value', average: 'AOV', ratio: 'ROAS' }
    }
    const plural = resultTypeLabel(model.terminalActionType)
    const singular = costPerResultTypeLabel(model.terminalActionType).replace(/^Per /, '').toLowerCase()
    return {
        count: plural,
        cost: `Cost / ${singular}`,
        value: `${plural} value`,
        average: `Value / ${singular}`,
        ratio: 'Return on spend',
    }
}

/**
 * What the grid needs to know about an advertiser to choose a default card face.
 *
 * Every field here is one the API's `meta.outcomeModel` actually carries, and it is read from that
 * response object directly rather than mapped into a surface-local shape. The previous version
 * declared a `hasTerminalEvent` boolean the server has never sent: the field read `undefined` on
 * every account, so the default face fell through to the purchase-shaped list for exactly the
 * advertisers the refinement exists to protect, and every `terminalEvent`-gated metric was hidden
 * from the picker for everyone. A field the response does not have cannot be recovered by a
 * plausible name — so the model names the server's own `terminalActionType` and derives the rest.
 */
export interface IGridOutcomeModel {
    /**
     * Whether the terminal event is a purchase — the gate for the purchase-calibrated bands AND for
     * the purchase vocabulary. Required, not optional: an absent one reads as `false`, which would
     * relabel an e-commerce account's whole card face off a field nobody set.
     */
    isPurchase: boolean
    /** Whether the account's terminal conversion event carries money — the gate for value metrics. */
    hasValue: boolean
    /** The account's terminal conversion event, or `null` where it reports none. */
    terminalActionType: string | null
}

/** Whether the account reports a terminal conversion event at all — the gate for count metrics. */
export function hasTerminalEvent(model: IGridOutcomeModel): boolean {
    return model.terminalActionType !== null && model.terminalActionType !== undefined
}

/**
 * The default card face for an account whose outcome carries no money.
 *
 * `cpa` rather than `costPerResult`, though only one of the two carries a correct label out of the
 * catalog. Cost-per-result divides by each AD SET's own optimisation result, so it is withheld
 * outright — `null`, an em-dash — the moment one creative ran across two objectives
 * (`creative-grouping.ts`, `mixedObjectives`), and the catalog gives it `verdict: { kind: 'none' }`
 * for the same reason: a lead, a ThruPlay and a purchase are not on one scale, so the column is not
 * comparable down the grid either. Putting it in a DEFAULT would reintroduce the em-dash this whole
 * refinement exists to remove. `cpa` divides by the ACCOUNT's one terminal event, so it is computed
 * for every creative and comparable across all of them — its label is the only thing that was
 * purchase-shaped, and `gridMetricLabel` fixes that at the point of render.
 */
export const NON_VALUE_METRIC_KEYS: IGridMetricKey[] = ['spend', 'results', 'cpa', 'impressions']

/**
 * Which of the five outcome labels a metric wears. The catalog stores ONE fixed string per metric —
 * `cpa` is the literal words `Cost / purchase` — while the number behind it is objective-agnostic,
 * so on a trial advertiser the figure is a cost per trial presented under a noun the account has
 * never converted on.
 */
const OUTCOME_LABEL_SLOT: Partial<Record<IGridMetricKey, keyof ReturnType<typeof outcomeMetricLabels>>> = {
    purchases: 'count',
    cpa: 'cost',
    purchaseValue: 'value',
    aov: 'average',
    roas: 'ratio',
}

/**
 * A card metric's label, in the terms of the event it was actually computed from.
 *
 * The one label path for the grid, the ranking row, the metric picker and the table header — the
 * four surfaces that render a card metric's name. They read this instead of `entry.label` so a
 * metric cannot be called one thing on a chip and another in the column header beside it, which is
 * exactly the drift that let the detail panel relabel correctly for a year while the grid did not.
 *
 * With no model this is the catalog's own string, so a first paint and a stale cached response both
 * render what they render today.
 */
export function gridMetricLabel(key: string, model?: IGridOutcomeModel | null, fallback?: string): string {
    // `||`, not `??`: `resultTypeLabel` returns an empty string for an action type that strips to
    // nothing, and an empty label renders a nameless chip rather than falling back.
    return outcomeMetricRename(key, model) || GRID_METRIC_BY_KEY[key as IGridMetricKey]?.label || fallback || key
}

/**
 * The account's own name for an outcome metric, or `null` where there is nothing to rename — the key
 * is not one of the five, or no model has arrived yet.
 *
 * Separate from `gridMetricLabel` because the table's headers already carry a label from the SERVER's
 * field catalog rather than the metric catalog, so it needs the rename without the fallback that
 * would overwrite that label with a different static string.
 */
export function outcomeMetricRename(key: string, model?: IGridOutcomeModel | null): string | null {
    // A purchase advertiser has nothing to rename: its vocabulary IS the catalog's. Renaming it
    // anyway is not a no-op — `outcomeMetricLabels` abbreviates `Average order value` to `AOV`, so
    // every e-commerce header, chip, tag and pill silently changed wording, and because the picker
    // renames before it searches, typing "average" stopped finding the metric at all.
    if (!model || model.isPurchase || !model.terminalActionType) return null
    const slot = OUTCOME_LABEL_SLOT[key as IGridMetricKey]
    return slot ? outcomeMetricLabels(model)[slot] : null
}

/**
 * The four metrics a card shows before the reader has chosen any, given what the account measures.
 *
 * Two of the four defaults — return and value — are purchase-shaped, so on the 511 of 1,625 spending
 * Meta accounts (31.4%) that report no purchases, HALF of every card's default face renders as an
 * em-dash. That is not a missing number the reader can act on; it is a card that looks broken.
 *
 * The shape is fixed at four with spend first, deliberately. Card layout and the selection summary
 * both assume a stable count, and changing the shape as well as the content would make this visible
 * to purchase accounts — which the whole change exists not to do. Only WHICH metrics appear moves.
 *
 * With no model this returns `DEFAULT_METRIC_KEYS` unchanged, so a first paint and a stale cached
 * response both render exactly what they render today.
 */
export function defaultMetricKeysFor(model?: IGridOutcomeModel | null): IGridMetricKey[] {
    if (!model || model.hasValue) return DEFAULT_METRIC_KEYS
    // No money on the terminal event, with or without one: return and value are unmeasurable either
    // way, so neither may sit in a default the reader never asked for. An account the poll has not
    // reached yet lands here too — it has no measured outcome at all, which is the strongest case
    // against defaulting it to two purchase columns that can only ever render em-dashes.
    return NON_VALUE_METRIC_KEYS
}

/**
 * The metric dropdown's trigger label. Names the picks in the order they were made (that is the order
 * a card renders them), folded to "first two +N" past two so the control keeps a fixed width whether
 * one metric is on or all six.
 */
export function metricSelectionSummary(keys: IGridMetricKey[]): string {
    // A key can arrive from a URL or a saved view written before the catalog changed. It is dropped
    // rather than summarised as `undefined`, which is what an unguarded index produced.
    const labels = keys.map((key) => GRID_METRIC_BY_KEY[key]?.label).filter((label): label is string => Boolean(label))
    if (labels.length === 0) return 'No metrics'
    if (labels.length <= 2) return labels.join(', ')
    return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`
}

/**
 * Chip tone for one cell. Takes the ROW (or a grouped header's aggregate projection) and nothing
 * else — the result set is deliberately absent from this signature (see the module comment).
 */
export function toneFor(def: IGridMetricDef, row: GroupAggregateRow): IMetricToneResult {
    return def.tone(row)
}

/**
 * Best-first. Every grid metric is a quantity where the interesting end is the top — the biggest
 * spend, the strongest return, the cheapest purchase read as its own ROAS — so a metric arriving at
 * the head of the ranking arrives descending. (Ascending is one more click away; `name` is the only
 * field that wants A→Z, and it is not a metric.)
 */
const METRIC_SORT_DIRECTION = 'desc'

/**
 * The ranking after a metric chip is clicked: that metric, and only that metric.
 *
 * REPLACES. The ranking is single-criterion, so whatever was applied before is gone rather than
 * kept behind as a tiebreaker — including a longer ranking a link or saved view carried in from
 * when ordered criteria were still offered, which collapses on the first click.
 *
 * Clicking the metric that ALREADY ranks the grid reverses it instead — the column-header idiom,
 * and the cheapest way to ask for "worst first". Any other metric arrives at the metric default
 * direction rather than one it happened to carry earlier: the click means "rank by this now", and
 * inheriting a direction set two clicks ago for a different purpose reads as a bug.
 */
export function replaceMetricSort(sorts: ICreativePerformanceSortRule[], field: ICreativePerformanceFieldId) {
    const applied = sorts[0]
    const direction = applied?.field === field ? (applied.direction === 'desc' ? 'asc' : 'desc') : METRIC_SORT_DIRECTION
    return [{ field, direction }] satisfies ICreativePerformanceSortRule[]
}

/**
 * The applied rule for a card metric, or `undefined` when the ranking is on something else. One
 * reader for a chip's disclosure, so a chip can never show an arrow for an order it is not part of.
 */
export function metricSortRule(sorts: ICreativePerformanceSortRule[], key: string) {
    return sorts.find((sort) => sort.field === key)
}
